import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import LoadingOverlay from '../components/LoadingOverlay';
import './EmployeePortalPage.css';
import './SignatureDialog.css';
import PortalDocuments from '../components/PortalDocuments';
import PortalLeaves from '../components/PortalLeaves';
import PortalMedicalCertificates from '../components/PortalMedicalCertificates';

interface Receipt {
  id: string;
  receiptNumber?: string;
  period: string;
  amount: number;
  fileName: string;
  status: string;
  signedAt?: string;
  signatureType?: 'CONFORMITY' | 'NON_CONFORMITY';
  signatureComment?: string;
}
type SignatureType = 'CONFORMITY' | 'NON_CONFORMITY';

export default function EmployeePortalPage(): React.ReactElement {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [items, setItems] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [signing, setSigning] = useState<Receipt | null>(null);
  const [signatureType, setSignatureType] = useState<SignatureType>('CONFORMITY');
  const [comment, setComment] = useState('');
  const load = async () => {
    try {
      setItems((await apiClient.get('/employee-portal/receipts')).data);
    } catch {
      setError('No pudimos cargar tus recibos.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const download = async (item: Receipt) => {
    setBusy('Preparando tu recibo…');
    setError('');
    try {
      const x = (await apiClient.get(`/employee-portal/receipts/${item.id}`)).data;
      const a = document.createElement('a');
      a.href = `data:${x.mimeType};base64,${x.documentData}`;
      a.download = x.fileName;
      a.click();
    } catch {
      setError('No se pudo descargar el recibo.');
    } finally {
      setBusy('');
    }
  };
  const openSignature = (item: Receipt) => {
    setSigning(item);
    setSignatureType('CONFORMITY');
    setComment('');
    setError('');
  };
  const sign = async () => {
    if (!signing) return;
    if (signatureType === 'NON_CONFORMITY' && !comment.trim()) {
      setError('Escribí el motivo de la disconformidad antes de firmar.');
      return;
    }
    setBusy('Registrando tu firma…');
    setError('');
    setSuccess('');
    try {
      await apiClient.post(`/employee-portal/receipts/${signing.id}/sign`, {
        type: signatureType,
        comment: signatureType === 'NON_CONFORMITY' ? comment.trim() : undefined,
      });
      setSigning(null);
      await load();
      setSuccess(
        signatureType === 'CONFORMITY'
          ? 'Tu firma en conformidad quedó registrada.'
          : 'Tu firma en disconformidad y comentario quedaron registrados.'
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo registrar la firma.');
    } finally {
      setBusy('');
    }
  };
  const exit = async () => {
    await logout();
    navigate('/login');
  };
  return (
    <div className="employee-portal">
      {busy && (
        <LoadingOverlay
          title={busy}
          detail="Guardando la operación de forma segura. No cierres esta ventana."
        />
      )}
      <header className="employee-header">
        <div>
          <span>Recibos Digitales</span>
          <strong>
            {user?.firstName} {user?.lastName}
          </strong>
        </div>
        <button onClick={() => void exit()}>Cerrar sesión</button>
      </header>
      <main className="employee-content">
        <section className="employee-welcome">
          <span className="employee-eyebrow">MI DOCUMENTACIÓN</span>
          <h1>Mi legajo digital</h1>
          <p>Consultá los documentos habilitados por tu empresa.</p>
        </section>
        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}
        {success && <div className="success-banner">{success}</div>}
        <PortalDocuments busy={setBusy} error={setError} />
        <PortalLeaves busy={setBusy} error={setError} />
        <PortalMedicalCertificates busy={setBusy} error={setError} />
        <h2 className="portal-section-title">Recibos de sueldo</h2>
        {loading ? (
          <div className="portal-empty">Cargando tus recibos…</div>
        ) : items.length === 0 ? (
          <div className="portal-empty">
            <strong>Todavía no tenés recibos disponibles</strong>
            <span>Cuando la empresa publique uno, aparecerá en esta pantalla.</span>
          </div>
        ) : (
          <div className="receipt-list">
            {items.map((x) => (
              <article className="receipt-card" key={x.id}>
                <div className="receipt-icon">PDF</div>
                <div className="receipt-info">
                  <span>{x.receiptNumber ? `RECIBO N° ${x.receiptNumber}` : 'PERÍODO'}</span>
                  <strong>{x.period}</strong>
                  <small>{x.fileName}</small>
                </div>
                <div className="receipt-amount">
                  <span>Importe neto</span>
                  <strong>
                    ${Number(x.amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </strong>
                </div>
                <div
                  className={`portal-status ${x.status === 'SIGNED' ? 'is-signed' : ''} ${x.signatureType === 'NON_CONFORMITY' ? 'is-observed' : ''}`}
                >
                  {x.status !== 'SIGNED'
                    ? 'Pendiente de firma'
                    : x.signatureType === 'NON_CONFORMITY'
                      ? 'Firmado en disconformidad'
                      : 'Firmado en conformidad'}
                </div>
                <div className="receipt-actions">
                  <button className="portal-secondary" onClick={() => void download(x)}>
                    Descargar
                  </button>
                  {x.status !== 'SIGNED' && (
                    <button className="portal-primary" onClick={() => openSignature(x)}>
                      Leer y firmar
                    </button>
                  )}
                </div>
                {x.signatureComment && (
                  <div className="signature-observation">
                    <strong>Observación registrada</strong>
                    <span>{x.signatureComment}</span>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
      {signing && (
        <div
          className="signature-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSigning(null);
          }}
        >
          <section
            className="signature-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="signature-title"
          >
            <span className="employee-eyebrow">FIRMA DEL RECIBO</span>
            <h2 id="signature-title">Elegí cómo querés firmar</h2>
            <p>
              Recibo N° {signing.receiptNumber || 'sin número'} · período {signing.period}
            </p>
            <div className="signature-options">
              <label className={signatureType === 'CONFORMITY' ? 'selected' : ''}>
                <input
                  type="radio"
                  name="signatureType"
                  checked={signatureType === 'CONFORMITY'}
                  onChange={() => setSignatureType('CONFORMITY')}
                />
                <span>
                  <strong>Firmar en conformidad</strong>
                  <small>Confirmo que recibí, leí y estoy conforme con el recibo.</small>
                </span>
              </label>
              <label className={signatureType === 'NON_CONFORMITY' ? 'selected warning' : ''}>
                <input
                  type="radio"
                  name="signatureType"
                  checked={signatureType === 'NON_CONFORMITY'}
                  onChange={() => setSignatureType('NON_CONFORMITY')}
                />
                <span>
                  <strong>Firmar en disconformidad</strong>
                  <small>Dejo constancia de que no estoy conforme y explico el motivo.</small>
                </span>
              </label>
            </div>
            {signatureType === 'NON_CONFORMITY' && (
              <label className="signature-comment">
                <span>Motivo de la disconformidad</span>
                <textarea
                  autoFocus
                  maxLength={2000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Describí claramente qué aspecto del recibo querés observar…"
                />
                <small>{comment.length}/2000</small>
              </label>
            )}
            <div className="signature-legal">
              La firma registra fecha, identidad, dirección IP, dispositivo, elección y comentario
              como evidencia de la operación.
            </div>
            <div className="signature-buttons">
              <button className="portal-secondary" onClick={() => setSigning(null)}>
                Cancelar
              </button>
              <button
                className={`portal-primary ${signatureType === 'NON_CONFORMITY' ? 'non-conformity' : ''}`}
                disabled={signatureType === 'NON_CONFORMITY' && !comment.trim()}
                onClick={() => void sign()}
              >
                Confirmar firma
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
